import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import { Tooltip } from '@fx-ui/fine-design';
import classNames from 'classnames';
import { isNumber, replace, size, trim, showMaxLengthTip } from '~/utils';
import Intl from '@fx-ui/intl';
import { UGC_MAX_LENGTH } from '~/constants';

interface ITitleEditorProps {
    className?: string;
    style?: CSSProperties;
    name: string;
    defaultName?: string;
    editable?: boolean;
    selectDefault?: boolean;
    onChange?: (obj: { name: string }) => void;
    maxLength?: number;
    onLengthOverLimit?: (maxLength: number) => void;
}

const { HEADER_TITLE } = UGC_MAX_LENGTH.NAV;

const TitleEditor: React.FunctionComponent<ITitleEditorProps> = ({
    className,
    style,
    defaultName = Intl.get('unnamed'),
    name,
    editable = true,
    selectDefault = true,
    onChange,
    maxLength = HEADER_TITLE,
    onLengthOverLimit = showMaxLengthTip
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState(name);
    const [showToolTip, setShowToolTip] = useState(false);
    const [inputFocus, setInputFocus] = useState(false);
    const lastValidVal = useRef<string>(name);

    useEffect(() => {
        if (selectDefault && name === defaultName && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
            setInputFocus(true);
            setShowToolTip(false);
        }
    }, []);

    useEffect(() => {
        if (value !== name) {
            setValue(name);
        }
    }, [name]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setInputFocus(false);
        const text = replace(trim(e.target.value), /\s{2,}/g, ' ');
        if (isNumber(maxLength) && size(text) > maxLength) {
            onLengthOverLimit(maxLength);
            setValue(lastValidVal.current);
            return;
        }
        const newName = text || defaultName;
        setValue(newName);
        if (newName === name) {
            return;
        }
        onChange && onChange({ name: newName });
    };

    // 回车键事件
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13) {
            inputRef.current && inputRef.current.blur();
        }
    };

    const handleFocus = () => {
        setInputFocus(true);
        setShowToolTip(false);
        lastValidVal.current = value;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value || '');
    };

    return (
        <div className={classNames('fx-title-editor', className)} style={style}>
            {editable && (
                <Tooltip
                    content={Intl.get('rename')}
                    visible={showToolTip}
                    onVisibleChange={(v) => setShowToolTip(inputFocus ? false : v)}
                >
                    {/* 嵌套一层是因为 Tooltip 会覆盖子元素的ref */}
                    <div className="input-container">
                        <input
                            ref={inputRef}
                            value={value}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            onChange={handleChange}
                            onFocus={handleFocus}
                        />
                    </div>
                </Tooltip>
            )}
            <span
                style={{ visibility: inputFocus ? 'hidden' : 'visible' }}
                title={editable ? '' : value}
            >
                {value}
            </span>
        </div>
    );
};

export default TitleEditor;
